import { Button } from "../ui/button";
import { ArrowUp, ArrowDown, Edit2, Trash2, GitCompare } from "lucide-react";
import type { FaqItem } from "./useFaqsManager";

interface FaqRowProps {
  faq: FaqItem;
  idx: number;
  totalCount: number;
  onEdit: (faq: FaqItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
  onViewDiff: (item: FaqItem) => void;
}

function FaqRow({
  faq,
  idx,
  totalCount,
  onEdit,
  onDelete,
  onMove,
  onViewDiff,
}: FaqRowProps) {
  return (
    <tr className="hover:bg-muted/10 transition-colors">
      <td className="p-4 text-center font-mono font-bold text-muted-foreground">
        {idx + 1}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="font-semibold text-foreground leading-snug">
          {faq.question_en}
        </p>
        {faq.question_tr && (
          <p className="text-xs text-muted-foreground italic leading-snug">
            {faq.question_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-md space-y-1">
        <p className="text-xs text-foreground/85 line-clamp-2 leading-relaxed">
          {faq.answer_en}
        </p>
        {faq.answer_tr && (
          <p className="text-[11px] text-muted-foreground/80 italic line-clamp-2 leading-relaxed">
            {faq.answer_tr}
          </p>
        )}
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "up")}
            disabled={idx === 0}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowUp className="h-4.5 w-4.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "down")}
            disabled={idx === totalCount - 1}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowDown className="h-4.5 w-4.5" />
          </Button>
        </div>
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => onViewDiff(faq)}
            title="Compare live vs draft"
            className="h-8 w-8 border-border/60 hover:bg-muted/40 text-muted-foreground hover:text-foreground"
          >
            <GitCompare className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onEdit(faq)}
            className="h-8 w-8 border-border/60 hover:bg-brand-primary/10 hover:text-brand-primary text-muted-foreground"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onDelete(faq.id)}
            className="h-8 w-8 border-border/60 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

interface FaqsListProps {
  faqs: FaqItem[];
  onEdit: (faq: FaqItem) => void;
  onDelete: (id: string) => void;
  onMove: (index: number, direction: "up" | "down") => void;
  onViewDiff: (item: FaqItem) => void;
}

export function FaqsList({
  faqs,
  onEdit,
  onDelete,
  onMove,
  onViewDiff,
}: FaqsListProps) {
  const sortedFaqs = [...faqs].sort((a, b) => a.sort_order - b.sort_order);

  if (sortedFaqs.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">
          No FAQ items configured. Click the button above to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse font-sans">
          <thead>
            <tr className="bg-muted/40 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-4 w-12 text-center">#</th>
              <th className="p-4">Questions (English / Turkish)</th>
              <th className="p-4">Answers (English / Turkish)</th>
              <th className="p-4 w-28 text-center">Order</th>
              <th className="p-4 w-36 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-sm">
            {sortedFaqs.map((faq, idx) => (
              <FaqRow
                key={faq.id}
                faq={faq}
                idx={idx}
                totalCount={sortedFaqs.length}
                onEdit={onEdit}
                onDelete={onDelete}
                onMove={onMove}
                onViewDiff={onViewDiff}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FaqsList;
